import { useQuery } from "@tanstack/react-query";
import api from "./api";

export interface NetSuiteTaxDropdownData {
  currency: { id: string; name: string; symbol?: string }[];
  placeOfSupply: { id: string; name: string }[];
  tdsSectionCodes: { id: string; name: string }[];
  hsnSacCodes: { id: string; name: string }[];
  taxNature: { id: string; name: string }[];
  natureOfDocument: { id: string; name: string }[];
}

export const emptyNetSuiteTaxDropdowns: NetSuiteTaxDropdownData = {
  currency: [],
  placeOfSupply: [],
  tdsSectionCodes: [],
  hsnSacCodes: [],
  taxNature: [],
  natureOfDocument: [],
};

export interface NetSuiteState {
  id: string;
  name: string;
  shortname?: string;
}

/**
 * Hook to fetch NetSuite's states/provinces for a given country code (e.g. "US", "IN"),
 * for the NetSuite-only vendor address State dropdown.
 * @param companyId - Company UUID
 * @param countryCode - NetSuite/ISO alpha-2 country code
 */
export const useGetNetSuiteStates = (companyId?: string, countryCode?: string) => {
  return useQuery({
    queryKey: ["netsuite-states", companyId, countryCode],
    queryFn: async () => {
      if (!companyId || !countryCode) return [] as NetSuiteState[];
      const response = await api.get("/netsuite/states", {
        params: { companyId, country: countryCode },
      });
      return (response.data?.data || []) as NetSuiteState[];
    },
    enabled: !!companyId && !!countryCode,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
};

/**
 * Hook to fetch the live Industry/Terms reference-list names straight from NetSuite —
 * shared by Client and Vendor forms, e.g. for the Payment Terms and Industry dropdowns.
 * @param listType - "Industry" | "Terms"
 * @param companyId - Company UUID (required — the NetSuite account to read from)
 */
export const useGetNetSuiteReferenceList = (
  listType: "Industry" | "Terms",
  companyId?: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ["netsuite-reference-list", listType, companyId],
    queryFn: async () => {
      const response = await api.get(`/netsuite/reference-list/${listType}`, { params: { companyId } });
      return (response.data?.data || []) as string[];
    },
    enabled: enabled && !!companyId,
    staleTime: 1000 * 60 * 2, // 2 minutes — this is a live NetSuite read, not a local cache
  });
};

/**
 * Hook to fetch NetSuite bill/tax dropdowns
 * @param companyId - Company UUID (required)
 * @param enabled - Whether to enable the query
 */
export const useGetNetSuiteTaxDropdowns = (companyId?: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: ["netsuite-tax-dropdowns", companyId],
    queryFn: async () => {
      if (!companyId) return emptyNetSuiteTaxDropdowns;
      const response = await api.get("/netsuite/bill-dropdowns", {
        params: { companyId },
      });
      return response.data?.data as NetSuiteTaxDropdownData;
    },
    enabled: enabled && !!companyId,
    staleTime: 1000 * 60 * 30, // 30 minutes
  });
};

/**
 * Validate if a client mapped to a NetSuite customer uses a specific subsidiary
 */
export const validateCustomerSubsidiary = async (
  companyId: string,
  clientId: string,
  subsidiaryId: string
): Promise<{ isValid: boolean }> => {
  const response = await api.get("/netsuite/validate-customer-subsidiary", {
    params: { companyId, clientId, subsidiaryId },
  });
  return response.data?.data || { isValid: false };
};

/**
 * Validate if a vendor mapped to a NetSuite vendor uses a specific subsidiary
 */
export const validateVendorSubsidiary = async (
  companyId: string,
  vendorId: string,
  subsidiaryId: string
): Promise<{ isValid: boolean }> => {
  const response = await api.get("/netsuite/validate-vendor-subsidiary", {
    params: { companyId, vendorId, subsidiaryId },
  });
  return response.data?.data || { isValid: false };
};
